/**
 * Admin tab — UFL Mobile (iter 89).
 *
 * Sections:
 *   1. Dashboard cards (rooms, queue, retention, ratings, tokens, teams)
 *   2. 24h activity histogram
 *   3. 4v4 Match history table (rooms) with filters + admin actions
 *   4. Teams admin table
 *   5. Danger zone — clear queue
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Zap, Users, Trophy, RefreshCcw, Star,
  Coins, Activity, Trash2, X, ShieldX, ChevronLeft, ChevronRight, Filter,
  AlertTriangle, MessageSquare,
} from "lucide-react";

export default function AdminUflTab() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/ufl/dashboard");
        if (!cancelled) setDash(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return (
    <div className="space-y-5" data-testid="admin-ufl-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-mono text-[13px] uppercase tracking-[0.25em] inline-flex items-center gap-2 text-[#09090B] dark:text-[#F4F4F5]">
          <Zap className="w-4 h-4 text-[#60A5FA]" /> UFL Mobile · Admin
        </h2>
        <button
          type="button"
          onClick={reload}
          data-testid="admin-ufl-refresh"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#FAFAFC] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
        >
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading && !dash ? (
        <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-[#60A5FA]" data-testid="admin-ufl-loading" />
      ) : !dash ? (
        <div className="text-center py-8 text-[#71717A] text-sm">No data yet.</div>
      ) : (
        <>
          <DashboardCards dash={dash} />
          <ActivityHistogram hours={dash.hourly_24h || []} peak={dash.peak_hour_utc} />
          <DangerZone onChanged={reload} queueSize={dash.queue_size} />
          <RoomsPanel onChanged={reload} refreshKey={refreshKey} />
          <TeamsPanel refreshKey={refreshKey} />
        </>
      )}
    </div>
  );
}

/* ===================================================================== */
function DashboardCards({ dash }) {
  const rooms = dash.rooms || {};
  return (
    <div className="space-y-3" data-testid="admin-ufl-dashboard">
      {/* Row 1 — Rooms */}
      <Row label="Rooms" icon={Trophy} accent="#60A5FA">
        <Stat label="Total" value={rooms.total} testid="ufl-stat-rooms-total" />
        <Stat label="Waiting" value={rooms.waiting} accent="#FBBF24" testid="ufl-stat-rooms-waiting" />
        <Stat label="Locked" value={rooms.locked} accent="#10B981" testid="ufl-stat-rooms-locked" />
        <Stat label="Closed" value={rooms.closed} testid="ufl-stat-rooms-closed" />
        <Stat label="Completed" value={rooms.completed} accent="#10B981" testid="ufl-stat-rooms-completed" />
        <Stat label="Abandoned" value={rooms.abandoned} accent="#EF4444" testid="ufl-stat-rooms-abandoned" />
        <Stat label="24h" value={rooms.created_24h} testid="ufl-stat-rooms-24h" />
        <Stat label="7d" value={rooms.created_7d} testid="ufl-stat-rooms-7d" />
      </Row>

      {/* Row 2 — Match quality */}
      <Row label="Match quality" icon={Activity} accent="#10B981">
        <Stat label="Completion %" value={`${dash.completion_rate_pct ?? 0}%`} accent="#10B981" testid="ufl-stat-completion" />
        <Stat label="Full 4v4 lock %" value={`${dash.full_room_lock_pct ?? 0}%`} testid="ufl-stat-fulllock" />
        <Stat
          label="Avg wait → lock"
          value={dash.avg_wait_to_lock_secs == null ? "—" : `${dash.avg_wait_to_lock_secs}s`}
          testid="ufl-stat-avg-wait"
        />
        <Stat label="Tokens awarded" value={dash.tokens_awarded_rooms} icon={Coins} testid="ufl-stat-tokens" />
        <Stat label="Ratings" value={dash.ratings?.total} icon={Star} testid="ufl-stat-ratings" />
        <Stat
          label="Avg credibility"
          value={dash.ratings?.avg_credibility == null ? "—" : dash.ratings.avg_credibility}
          accent="#FBBF24"
          testid="ufl-stat-avg-cred"
        />
      </Row>

      {/* Row 3 — Live & queue */}
      <Row label="Live activity" icon={Users} accent="#FBBF24">
        <Stat label="Active players" value={dash.active_players} testid="ufl-stat-active" />
        <Stat label="Queue size" value={dash.queue_size} accent="#FBBF24" testid="ufl-stat-queue" />
        <Stat label="Peak hour UTC" value={dash.peak_hour_utc == null ? "—" : `${String(dash.peak_hour_utc).padStart(2,"0")}:00`} testid="ufl-stat-peak" />
        <Stat label="UFL ID submitted" value={dash.usernames?.submitted} testid="ufl-stat-uname-sub" />
        <Stat label="UFL ID missing" value={dash.usernames?.missing} accent="#EF4444" testid="ufl-stat-uname-miss" />
        <Stat label="UFL ID %" value={`${dash.usernames?.submitted_pct ?? 0}%`} testid="ufl-stat-uname-pct" />
      </Row>

      {/* Row 4 — Retention + Teams */}
      <Row label="Retention & teams" icon={Trophy} accent="#A855F7">
        <Stat label="Players ever" value={dash.retention?.total_players_ever} testid="ufl-stat-players-ever" />
        <Stat label="Repeat players" value={dash.retention?.repeat_players} testid="ufl-stat-repeat" />
        <Stat label="Retention %" value={`${dash.retention?.retention_pct ?? 0}%`} accent="#A855F7" testid="ufl-stat-retention" />
        <Stat label="Teams" value={dash.teams?.total} testid="ufl-stat-teams" />
        <Stat label="Pending invites" value={dash.teams?.pending_invites} testid="ufl-stat-team-inv" />
        <Stat label="Pending requests" value={dash.teams?.pending_requests} testid="ufl-stat-team-req" />
      </Row>
    </div>
  );
}

function Row({ label, icon: Icon, accent, children }) {
  return (
    <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2 mb-3" style={{ color: accent }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "#60A5FA", icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-[#E4E4E7]/60 dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#0F0F12] p-3" data-testid={testid}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A] flex items-center gap-1">
        {Icon && <Icon className="w-2.5 h-2.5" />} {label}
      </div>
      <div className="text-xl font-extrabold tabular-nums mt-0.5" style={{ color: accent }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

/* ===================================================================== */
function ActivityHistogram({ hours, peak }) {
  const max = useMemo(() => Math.max(1, ...(hours.map((h) => h.count) || [1])), [hours]);
  return (
    <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4" data-testid="admin-ufl-histogram">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#60A5FA] inline-flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Rooms created · last 24h (UTC)
        </div>
        {peak != null && (
          <div className="text-[10px] font-mono text-[#71717A]">
            Peak: <span className="text-[#FBBF24] font-bold">{String(peak).padStart(2,"0")}:00 UTC</span>
          </div>
        )}
      </div>
      <div className="flex items-end gap-1 h-28">
        {hours.map((h) => (
          <div key={h.hour} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${h.hour}:00 UTC · ${h.count} rooms`}>
            <div
              className="w-full rounded-t"
              style={{
                height: `${(h.count / max) * 100}%`,
                minHeight: h.count ? "2px" : "0",
                background: h.hour === peak
                  ? "linear-gradient(180deg, #FBBF24, #F59E0B)"
                  : "linear-gradient(180deg, #60A5FA, #3B82F6)",
              }}
            />
            <div className="text-[8px] font-mono text-[#71717A]">{h.hour}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===================================================================== */
function DangerZone({ onChanged, queueSize }) {
  const [busy, setBusy] = useState(false);
  const clearQueue = async () => {
    if (!window.confirm(`Clear the entire matchmaking queue (${queueSize} player${queueSize === 1 ? "" : "s"})?\n\nThey'll need to rejoin manually.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ufl/queue/clear");
      toast.success(`Queue cleared (${data.removed} removed)`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#EF4444]/40 bg-gradient-to-br from-[#EF4444]/5 to-transparent p-4" data-testid="admin-ufl-danger-zone">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#EF4444] inline-flex items-center gap-1.5 mb-2">
        <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={clearQueue}
          disabled={busy || queueSize === 0}
          data-testid="admin-ufl-clear-queue"
          className="inline-flex items-center gap-2 rounded-lg bg-[#EF4444] text-white font-mono text-[11px] uppercase tracking-wider px-3 py-2 hover:bg-[#DC2626] disabled:opacity-40 transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldX className="w-3.5 h-3.5" />}
          Clear queue ({queueSize ?? 0})
        </button>
      </div>
    </div>
  );
}

/* ===================================================================== */
function RoomsPanel({ onChanged, refreshKey }) {
  const [data, setData] = useState({ rooms: [], total: 0 });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      params.set("limit", String(LIMIT));
      params.set("offset", String(offset));
      const { data: d } = await api.get(`/admin/ufl/rooms?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status, q, offset]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const total = data.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const page = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4" data-testid="admin-ufl-rooms">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#60A5FA] inline-flex items-center gap-2 mb-3">
        <Trophy className="w-3.5 h-3.5" /> 4v4 Match History · {total} rooms
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          data-testid="admin-ufl-rooms-status"
          className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono"
        >
          <option value="">All statuses</option>
          <option value="waiting">Waiting</option>
          <option value="locked">Locked</option>
          <option value="closed">Closed</option>
        </select>
        <form
          onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); setOffset(0); }}
          className="flex items-center gap-1"
        >
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search by username…"
            data-testid="admin-ufl-rooms-search"
            className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono w-44"
          />
          <button
            type="submit"
            className="inline-flex items-center gap-1 rounded-md bg-[#60A5FA] text-[#020617] font-mono text-[10px] uppercase tracking-wider px-2 py-1.5"
          >
            <Filter className="w-3 h-3" /> Filter
          </button>
        </form>
        {(status || q) && (
          <button
            type="button"
            onClick={() => { setStatus(""); setQ(""); setQInput(""); setOffset(0); }}
            className="text-[10px] font-mono text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin mx-auto my-6 text-[#60A5FA]" />
      ) : (data.rooms || []).length === 0 ? (
        <div className="text-center py-8 text-[#71717A] text-[12px]">No rooms found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#71717A]">
                <th className="text-left p-2">Room</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Members</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Duration</th>
                <th className="text-left p-2">Ratings</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.rooms.map((r) => (
                <RoomRow
                  key={r.id}
                  room={r}
                  onOpen={() => setDetail(r.id)}
                  onChanged={() => { onChanged && onChanged(); load(); }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-[10px] font-mono text-[#71717A]">
        <div data-testid="admin-ufl-rooms-pageinfo">Page {page} of {pages}</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={offset === 0}
            className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1 hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F12] disabled:opacity-30"
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + LIMIT)}
            disabled={page >= pages}
            className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1 hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F12] disabled:opacity-30"
          >
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {detail && (
        <RoomDetailModal
          roomId={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { onChanged && onChanged(); load(); }}
        />
      )}
    </div>
  );
}

function RoomRow({ room, onOpen, onChanged }) {
  const [busy, setBusy] = useState(false);
  const close = async () => {
    if (!window.confirm("Force-close this room? Chat will be purged.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/ufl/rooms/${room.id}/close`, null, { params: { reason: "admin_close" } });
      toast.success("Room closed");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const statusColor = {
    waiting: "#FBBF24",
    locked: "#10B981",
    closed: "#71717A",
  }[room.status] || "#71717A";

  return (
    <tr className="border-t border-[#E4E4E7] dark:border-[#2A2A30] hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F12]">
      <td className="p-2 align-top">
        <button
          type="button"
          onClick={onOpen}
          data-testid={`admin-ufl-room-open-${room.id}`}
          className="text-[10px] font-mono text-[#60A5FA] hover:underline"
          title="View detail"
        >
          {room.id.slice(0, 8)}…
        </button>
      </td>
      <td className="p-2 align-top">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-widest" style={{ color: statusColor, backgroundColor: `${statusColor}20` }}>
          {room.status}
        </span>
        {room.is_full_lock && (
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-widest text-[#10B981] bg-[#10B98120]">4v4</span>
        )}
        {room.abandoned && (
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-widest text-[#EF4444] bg-[#EF444420]">abandoned</span>
        )}
        {room.token_awarded && (
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-widest text-[#FBBF24] bg-[#FBBF2420]">🪙</span>
        )}
      </td>
      <td className="p-2 align-top">
        <div className="flex flex-wrap gap-1">
          {(room.members || []).map((m) => (
            <span key={m.user_id} className="inline-flex items-center gap-1 rounded-full bg-[#FAFAFC] dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] px-1.5 py-0.5 text-[10px]">
              <span>{m.avatar_emoji || "⚽"}</span>
              <span className="font-semibold">@{m.username}</span>
              {m.ufl_username && <span className="text-[#71717A]">· UFL: {m.ufl_username}</span>}
            </span>
          ))}
          <span className="text-[10px] text-[#71717A]">({room.member_count}/4)</span>
        </div>
      </td>
      <td className="p-2 align-top text-[10px] text-[#71717A] whitespace-nowrap">
        {room.created_at ? new Date(room.created_at).toLocaleString() : "—"}
      </td>
      <td className="p-2 align-top text-[10px] text-[#71717A] tabular-nums">
        {room.duration_secs == null ? "—" : `${Math.floor(room.duration_secs / 60)}m ${room.duration_secs % 60}s`}
      </td>
      <td className="p-2 align-top text-[10px] text-[#71717A] tabular-nums">
        {room.ratings?.got ?? 0}/{room.ratings?.needed ?? 0}
      </td>
      <td className="p-2 align-top text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onOpen}
          data-testid={`admin-ufl-room-detail-${room.id}`}
          className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1 text-[10px] uppercase tracking-wider text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] mr-1"
        >
          <MessageSquare className="w-3 h-3" /> Detail
        </button>
        {room.status !== "closed" && (
          <button
            type="button"
            onClick={close}
            disabled={busy}
            data-testid={`admin-ufl-room-close-${room.id}`}
            className="inline-flex items-center gap-1 rounded-md bg-[#EF4444]/15 text-[#EF4444] px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-[#EF4444]/25 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Close
          </button>
        )}
      </td>
    </tr>
  );
}

function RoomDetailModal({ roomId, onClose, onChanged }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ufl/rooms/${roomId}`);
      setRoom(data);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    finally { setLoading(false); }
  }, [roomId, onClose]);
  useEffect(() => { load(); }, [load]);

  const kick = async (uid, uname) => {
    if (!window.confirm(`Kick @${uname} from this room?`)) return;
    try {
      await api.post(`/admin/ufl/rooms/${roomId}/kick`, null,
        { params: { user_id: uid, reason: "admin_kick" } });
      toast.success(`Kicked @${uname}`);
      onChanged && onChanged();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="admin-ufl-room-modal">
      <div
        className="bg-white dark:bg-[#1A1A1E] rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#E4E4E7] dark:border-[#2A2A30] p-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#60A5FA]">
            Room · {roomId.slice(0, 8)}…
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-ufl-room-modal-close"
            className="text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {loading || !room ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-[#60A5FA]" />
        ) : (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] font-mono">
              <KV k="Status" v={room.status} />
              <KV k="Created" v={room.created_at && new Date(room.created_at).toLocaleString()} />
              <KV k="First two" v={room.first_two_at && new Date(room.first_two_at).toLocaleString()} />
              <KV k="Locked at" v={room.locked_at && new Date(room.locked_at).toLocaleString()} />
              <KV k="Closes at" v={room.closes_at && new Date(room.closes_at).toLocaleString()} />
              <KV k="Closed at" v={room.closed_at && new Date(room.closed_at).toLocaleString()} />
              <KV k="Token awarded" v={room.token_awarded ? "Yes" : "No"} />
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">Members ({room.members?.length || 0})</div>
              <div className="space-y-1.5">
                {(room.members || []).map((m) => (
                  <div key={m.user_id} className="flex items-center justify-between gap-2 rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5 text-[11px]" data-testid={`admin-ufl-room-member-${m.user_id}`}>
                    <div className="flex items-center gap-2">
                      <span>{m.avatar_emoji || "⚽"}</span>
                      <span className="font-semibold">@{m.username}</span>
                      {m.ufl_username && <span className="text-[#71717A]">· UFL: {m.ufl_username}</span>}
                    </div>
                    {room.status !== "closed" && (
                      <button
                        type="button"
                        onClick={() => kick(m.user_id, m.username)}
                        data-testid={`admin-ufl-room-kick-${m.user_id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-[#EF4444]/15 text-[#EF4444] px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-[#EF4444]/25"
                      >
                        <ShieldX className="w-3 h-3" /> Kick
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {(room.ratings || []).length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">Ratings ({room.ratings.length})</div>
                <div className="space-y-1 text-[10px] font-mono text-[#71717A]">
                  {room.ratings.map((r) => (
                    <div key={r.id}>
                      <span>{r.rater_id.slice(0,8)} → {r.ratee_id.slice(0,8)}</span>
                      <span className="ml-2 text-[#FBBF24]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(room.messages || []).length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">Chat ({room.messages.length})</div>
                <div className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] p-2 max-h-48 overflow-y-auto space-y-1 text-[11px]">
                  {room.messages.map((m) => (
                    <div key={m.id}>
                      <span className="font-bold text-[#60A5FA]">@{m.username}</span>
                      <span className="text-[#71717A] ml-2 text-[9px]">{new Date(m.created_at).toLocaleString()}</span>
                      <div className="whitespace-pre-wrap text-[#09090B] dark:text-[#F4F4F5]">{m.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="rounded-md bg-[#FAFAFC] dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-[#71717A]">{k}</div>
      <div className="text-[11px] text-[#09090B] dark:text-[#F4F4F5] truncate" title={v || ""}>{v || "—"}</div>
    </div>
  );
}

/* ===================================================================== */
function TeamsPanel({ refreshKey }) {
  const [data, setData] = useState({ teams: [], total: 0 });
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("limit", String(LIMIT));
      params.set("offset", String(offset));
      const { data: d } = await api.get(`/admin/ufl/teams?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, offset]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (t) => {
    if (!window.confirm(`Delete team "${t.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/ufl/teams/${t.id}`);
      toast.success(`Deleted "${t.name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = data.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const page = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4" data-testid="admin-ufl-teams">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A855F7] inline-flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Teams · {total}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); setOffset(0); }}
          className="flex items-center gap-1"
        >
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search team name…"
            data-testid="admin-ufl-teams-search"
            className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono w-44"
          />
          <button type="submit" className="inline-flex items-center gap-1 rounded-md bg-[#A855F7] text-white font-mono text-[10px] uppercase tracking-wider px-2 py-1.5">
            <Filter className="w-3 h-3" /> Filter
          </button>
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setQInput(""); setOffset(0); }}
              className="text-[10px] font-mono text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin mx-auto my-6 text-[#A855F7]" />
      ) : (data.teams || []).length === 0 ? (
        <div className="text-center py-8 text-[#71717A] text-[12px]">No teams found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#71717A]">
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Captain</th>
                <th className="text-left p-2">Members</th>
                <th className="text-left p-2">Updated</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.teams.map((t) => (
                <tr key={t.id} className="border-t border-[#E4E4E7] dark:border-[#2A2A30] hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F12]" data-testid={`admin-ufl-team-${t.id}`}>
                  <td className="p-2 align-top text-[#09090B] dark:text-[#F4F4F5] font-semibold">{t.name}</td>
                  <td className="p-2 align-top">@{t.captain?.username || "?"}</td>
                  <td className="p-2 align-top tabular-nums">{t.member_count}</td>
                  <td className="p-2 align-top text-[10px] text-[#71717A]">{t.updated_at ? new Date(t.updated_at).toLocaleString() : "—"}</td>
                  <td className="p-2 align-top text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => remove(t)}
                      data-testid={`admin-ufl-team-delete-${t.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-[#EF4444]/15 text-[#EF4444] px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-[#EF4444]/25"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 text-[10px] font-mono text-[#71717A]">
        <div>Page {page} of {pages}</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={offset === 0}
            className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1 hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F12] disabled:opacity-30"
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <button
            type="button"
            onClick={() => setOffset(offset + LIMIT)}
            disabled={page >= pages}
            className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1 hover:bg-[#FAFAFC] dark:hover:bg-[#0F0F12] disabled:opacity-30"
          >
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
